"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useState } from "react";
import { FaXmark } from "react-icons/fa6";
import { FiZoomIn, FiZoomOut } from "react-icons/fi";
import { TransformComponent, TransformWrapper, useControls } from "react-zoom-pan-pinch";
import Button from "@/components/ui/button";
import { Dialog, DialogBody, DialogClose, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { cx } from "@/lib/tailwind-utils";
interface ChatImageAttachmentsProps {
  images: string[];
  /** `grid` = square tiles (inbox); `inline` = wrapped, height-capped (portal). */
  layout?: "grid" | "inline";
  /**
   * Mints a fresh URL for an image that failed to load. Only the surfaces that
   * can sign URLs pass this; the rest just show a broken image.
   */
  onRefreshUrl?: (failedUrl: string) => Promise<string | null | undefined>;
  /** Overlay controls revealed on hover, e.g. download or save-to-gallery. */
  renderTileActions?: (imageUrl: string) => React.ReactNode;
  /** Controls in the fullscreen dialog header. */
  renderViewerActions?: (imageUrl: string) => React.ReactNode;
  tileClassName?: string;
}
function ZoomControls(t0) {
  const $ = _c(16);
  const {
    className
  } = t0;
  const {
    zoomIn,
    zoomOut
  } = useControls();
  let t1;
  if ($[0] !== className) {
    t1 = cx("absolute right-2 top-2 z-10 flex gap-2 p-2", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== zoomIn) {
    t2 = () => zoomIn();
    $[2] = zoomIn;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <FiZoomIn />;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <Button variant="secondary" className="h-8 w-8 rounded-full border-white/40 bg-background/60 p-0" onClick={t2} icon={t3} />;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== zoomOut) {
    t5 = () => zoomOut();
    $[7] = zoomOut;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <FiZoomOut />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5) {
    t7 = <Button variant="secondary" className="h-8 w-8 rounded-full border-white/40 bg-background/60 p-0" onClick={t5} icon={t6} />;
    $[10] = t5;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t1 || $[13] !== t4 || $[14] !== t7) {
    t8 = <div className={t1}>{t4}{t7}</div>;
    $[12] = t1;
    $[13] = t4;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}

/** Image attachments with a shared fullscreen zoom/pan viewer. */
export function ChatImageAttachments(t0) {
  const $ = _c(59);
  const {
    images,
    layout: t1,
    onRefreshUrl,
    renderTileActions,
    renderViewerActions,
    tileClassName
  } = t0;
  const layout = t1 === undefined ? "grid" : t1;
  const [fullscreenImage, setFullscreenImage] = useState(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {};
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [refreshedUrls, setRefreshedUrls] = useState(t2);
  let t3;
  if ($[1] !== images || $[2] !== refreshedUrls) {
    t3 = index => refreshedUrls[index] ?? images[index];
    $[1] = images;
    $[2] = refreshedUrls;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const getImageUrl = t3;
  let t4;
  if ($[4] !== onRefreshUrl || $[5] !== refreshedUrls) {
    t4 = async (index_0, failedUrl) => {
      if (!onRefreshUrl || refreshedUrls[index_0]) {
        return;
      }
      try {
        const newUrl = await onRefreshUrl(failedUrl);
        if (newUrl) {
          setRefreshedUrls(prev => ({
            ...prev,
            [index_0]: newUrl
          }));
        }
      } catch {}
    };
    $[4] = onRefreshUrl;
    $[5] = refreshedUrls;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleImageError = t4;
  let t5;
  if ($[7] !== fullscreenImage || $[8] !== onRefreshUrl) {
    t5 = async () => {
      if (!onRefreshUrl || !fullscreenImage) {
        return;
      }
      try {
        const newUrl_0 = await onRefreshUrl(fullscreenImage);
        if (newUrl_0) {
          setFullscreenImage(newUrl_0);
        }
      } catch {}
    };
    $[7] = fullscreenImage;
    $[8] = onRefreshUrl;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleFullscreenImageError = t5;
  if (!images || images.length === 0) {
    return null;
  }
  const isGrid = layout === "grid";
  const t6 = isGrid && images.length === 1 ? "overflow-hidden rounded-lg" : "";
  let t7;
  if ($[10] !== t6) {
    t7 = cx(t6);
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const t8 = isGrid ? "gap-px" : "flex flex-wrap gap-2";
  const t9 = isGrid && (images.length === 1 ? "flex" : "grid grid-cols-2 overflow-hidden rounded-lg");
  let t10;
  if ($[12] !== t8 || $[13] !== t9) {
    t10 = cx(t8, t9);
    $[12] = t8;
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== getImageUrl || $[16] !== handleImageError || $[17] !== images || $[18] !== isGrid || $[19] !== renderTileActions || $[20] !== tileClassName) {
    let t12;
    if ($[22] !== getImageUrl || $[23] !== handleImageError || $[24] !== images.length || $[25] !== isGrid || $[26] !== renderTileActions || $[27] !== tileClassName) {
      t12 = (image, index_1) => <div key={index_1} className="group relative"><div role="button" aria-label="Expand attachment" onClick={() => setFullscreenImage(getImageUrl(index_1))} className={cx("cursor-pointer overflow-hidden transition-opacity hover:opacity-90", isGrid ? images.length === 1 && "rounded-lg" : "rounded-lg border", tileClassName)} style={isGrid ? {
          width: "250px",
          height: "250px"
        } : undefined}><img src={getImageUrl(index_1)} alt="Attached image" className={cx(isGrid ? "h-full w-full object-cover" : "max-h-48 w-auto object-contain transition-transform hover:scale-[1.02]")} onError={() => handleImageError(index_1, getImageUrl(index_1))} /></div>{renderTileActions && <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">{renderTileActions(getImageUrl(index_1))}</div>}</div>;
      $[22] = getImageUrl;
      $[23] = handleImageError;
      $[24] = images.length;
      $[25] = isGrid;
      $[26] = renderTileActions;
      $[27] = tileClassName;
      $[28] = t12;
    } else {
      t12 = $[28];
    }
    t11 = images.map(t12);
    $[15] = getImageUrl;
    $[16] = handleImageError;
    $[17] = images;
    $[18] = isGrid;
    $[19] = renderTileActions;
    $[20] = tileClassName;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[29] !== t10 || $[30] !== t11) {
    t12 = <div className={t10}>{t11}</div>;
    $[29] = t10;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t12 || $[33] !== t7) {
    t13 = <div className={t7}>{t12}</div>;
    $[32] = t12;
    $[33] = t7;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  const t14 = !!fullscreenImage;
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = open => {
      if (!open) {
        setFullscreenImage(null);
      }
    };
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <DialogTitle className="sr-only">Fullscreen Image</DialogTitle>;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== fullscreenImage || $[38] !== renderViewerActions) {
    t17 = fullscreenImage && renderViewerActions?.(fullscreenImage);
    $[37] = fullscreenImage;
    $[38] = renderViewerActions;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== t17) {
    t18 = <span className="contents">{t17}</span>;
    $[40] = t17;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <DialogClose asChild={true}><Button variant="ghost" icon={<FaXmark size={20} />} /></DialogClose>;
    $[42] = t19;
  } else {
    t19 = $[42];
  }
  let t20;
  if ($[43] !== t18) {
    t20 = <DialogHeader className="flex-row justify-end">{t16}{t18}{t19}</DialogHeader>;
    $[43] = t18;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== fullscreenImage || $[46] !== handleFullscreenImageError) {
    t21 = fullscreenImage && <div className="relative flex h-full w-full items-center justify-center overflow-hidden"><TransformWrapper>{() => <><ZoomControls /><TransformComponent wrapperClass="!w-full !h-full" contentClass="!w-full !h-full items-center justify-center"><img src={fullscreenImage} alt="Full screen view" className="max-h-full max-w-full object-contain" onError={handleFullscreenImageError} /></TransformComponent></>}</TransformWrapper></div>;
    $[45] = fullscreenImage;
    $[46] = handleFullscreenImageError;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t21) {
    t22 = <DialogBody className="m-0 p-0">{t21}</DialogBody>;
    $[48] = t21;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== t20 || $[51] !== t22) {
    t23 = <DialogContent className="h-screen max-h-screen w-screen max-w-none">{t20}{t22}</DialogContent>;
    $[50] = t20;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] !== t14 || $[54] !== t23) {
    t24 = <Dialog open={t14} onOpenChange={t15}>{t23}</Dialog>;
    $[53] = t14;
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t13 || $[57] !== t24) {
    t25 = <>{t13}{t24}</>;
    $[56] = t13;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  return t25;
}